import { useEffect, useState } from 'react';
import { useQueryClient, type QueryClient } from '@tanstack/react-query';
import type { TaskRequestRecord } from '../v1';

/**
 * The dispatch-request cache, as something a whole TABLE can read.
 *
 * Its own module, apart from `useTaskRequest`, because the component
 * tests replace that hook wholesale; a key builder living beside it
 * would vanish with it and take every screen that sorts on the cache
 * down with the mock.
 */
const ROOT = 'task-request';

/** One cache entry per task, shared by every reader: the drawer, the
 *  overview's locate link and the Tasks table's Location column all
 *  ask the same question and must not each pay for the answer. */
export function taskRequestQueryKey(siteId: string, taskId: string | null) {
  return [ROOT, siteId, taskId] as const;
}

/**
 * What this browser already knows of one task's dispatch request,
 * WITHOUT asking: the record; `null` when it was asked and none is on
 * file, or when the read failed -- a table cell shows both as the same
 * dash; `undefined` when it has not been read, or is still on its way.
 *
 * Never fetches, and does not subscribe: the readers that decide what is
 * worth fetching are the cells, one row at a time. The Tasks table reads
 * this once, when someone picks the Location sort -- following each
 * read as it lands is what made that sort read every row in the list
 * (see `TasksScreen`).
 */
export function readCachedTaskRequest(
  qc: QueryClient,
  siteId: string,
  taskId: string,
): TaskRequestRecord | null | undefined {
  const state = qc.getQueryState<TaskRequestRecord | null>(taskRequestQueryKey(siteId, taskId));
  if (state?.status === 'success') return state.data ?? null;
  if (state?.status === 'error') return null;
  return undefined;
}

/**
 * A number that changes whenever a dispatch request lands in the cache.
 *
 * For a whole-list reader that has to re-read the cache when a row's
 * request arrives -- the Tasks type filter, which files a queued clean
 * under its mode once a cell has read it. Follows only reads that
 * SUCCEED, and causes none: what is fetched is still each cell's call.
 */
export function useTaskRequestCacheVersion(): number {
  const qc = useQueryClient();
  const [version, setVersion] = useState(0);
  useEffect(
    () =>
      qc.getQueryCache().subscribe((event) => {
        if (
          event.type === 'updated' &&
          event.action.type === 'success' &&
          event.query.queryKey[0] === ROOT
        ) {
          setVersion((v) => v + 1);
        }
      }),
    [qc],
  );
  return version;
}
